import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { Card, Icon } from 'antd';
import { TimeUtil } from "../../utils/time";
import useFetchMultipleRegionsLoadingTime from '../../siteSpeed/hooks/useFetchMultipleRegionsLoadingTime';
import { DASHBOARD_LONG_POLLING_INTERVAL_IN_MS } from '../../settings/config';
import SkeletonBox from '../SkeletonBox';
import CardTitle from '../CardTitle';
import { useFetchMonitorRegionsQuery } from '../../trackedWebsites/service';
import SiteSpeedChart from './SiteSpeedChart';

type Props = {
    websiteGuid: string;
    url: string;
    regions: string[];
    shouldLongPoll?: boolean;
};

export default function SiteSpeed({ websiteGuid, url, regions, shouldLongPoll }: Props) {
    const navigate = useNavigate();

    const currentDate = TimeUtil.utcNow();
    const sevenDaysAgo = TimeUtil.subtractDays(currentDate, 7);

    const [hasData, setHasData] = useState<boolean>(false);

    const { groupedLoadingTimes, isLoadingMultipleLoadingTimes } = useFetchMultipleRegionsLoadingTime({
        url,
        regions,
        interval: 'last_7_days',
        groupBy: 'day',
        startDate: sevenDaysAgo.format('YYYY-MM-DDTHH:mm:ss'),
        endDate: currentDate.format('YYYY-MM-DDTHH:mm:ss'),
        pollingInterval: !hasData && shouldLongPoll ? DASHBOARD_LONG_POLLING_INTERVAL_IN_MS : 0,
    });

    const { data: monitorRegions, isLoading: isLoadingRegions } = useFetchMonitorRegionsQuery();

    useEffect(() => {
        if (groupedLoadingTimes && !hasData) {
            const regionCodes = Object.keys(groupedLoadingTimes);
            if (regionCodes.length === regions.length && regionCodes.every((region) => groupedLoadingTimes[region].reports.length > 0)) {
                setHasData(true);
            }
        }
    }, [hasData, groupedLoadingTimes, regions.length]);

    const chartData = useMemo(
        () =>
            groupedLoadingTimes && monitorRegions
                ? Object.keys(groupedLoadingTimes)
                    .map((region) =>
                        groupedLoadingTimes[region].reports.map((report) => ({
                            c_at: report.c_at,
                            value: report.loading_time,
                            region: monitorRegions.regions[region],
                        }))
                    )
                    .flat()
                : [],
        [groupedLoadingTimes, monitorRegions]
    );

    const isLoading = isLoadingMultipleLoadingTimes || isLoadingRegions;

    return (
        <div className="dashboard-site-speed-box">
            {isLoading && <SkeletonBox />}
            {!isLoading && (
                <Card
                    data-cy="site-speed-box"
                    title={
                        <CardTitle
                            title="Loading time"
                            description="Over the last 7 days"
                        />
                    }
                    extra={
                        <div
                            className="view-report-btn"
                            onClick={() => navigate(`/site-speed?w_guid=${websiteGuid}`)}
                        >
                            <span>View report</span>
                            <Icon type="right" />
                        </div>
                    }
                >
                    <SiteSpeedChart
                        chartData={chartData}
                        regions={monitorRegions ? regions.map((regionCode) => monitorRegions.regions[regionCode]) : []}
                    />
                </Card>
            )}
        </div>
    );
}
